import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/* ── Privacy Policy sections — exact wording from cyrusfieldtech.com/kyo/kcl/privacy-policy ── */
const SECTIONS = [
  {
    number: "1.",
    heading: "Overview of This Policy",
    items: [
      {
        type: "numbered",
        label: "1.1",
        text: `This Privacy Policy (this "Policy") relates to the personal information that you submit when you purchase items through our website and other information we automatically collect when you access the website. This Policy explains all activities regarding your information, including but not limited to how we collect, use, disclose, and secure it and the rights available to you.`,
      },
      {
        type: "numbered",
        label: "1.2",
        text: "This website and this Policy are provided, controlled and maintained by KYo with contact email privacy@kyoown.com",
        emailLink: { label: "privacy@kyoown.com", href: "mailto:privacy@kyoown.com" },
      },
      {
        type: "numbered",
        label: "1.3",
        text: `You are required to read, understand and agree to the manner in which we process (collection, use, disclosure, security, etc.) your information in this Policy. You are also required to read our Terms and Conditions and print a copy each for future reference. By your use of the website and your submission of the requested personal information, you hereby agree to the manner in which we process your information as described in this Policy. If you do not agree, you should not submit information or purchase a membership plan from us.`,
      },
    ],
  },
  {
    number: "2.",
    heading: "What Information Do We Collect From You?",
    items: [
      {
        type: "para",
        text: `When you purchase a membership plan from us or access the website, we collect certain personal information which is described below. The information we collect from you depends on what such information is needed for. We collect the following types of personal information:`,
      },
      {
        type: "bullet-bold",
        bullets: [
          {
            key: "Personal Information you submit to us:",
            text: `Personal Information you submit to us is any information that you willingly submit to us when we require it. This information includes but is not limited to your names, email address, phone number, payment information, country, state, street, zip code, and city/town. This personal information may be collected when you (i) provide you register on our website, (ii) submit your payment information for payment, (iii) reach out to us regarding complaints or questions, (iv) sign up to receive newsletters from us, and (v) take part in surveys, promotions, and bonuses offered by KYo.`,
          },
          {
            key: "Personal Information collected automatically:",
            text: `This is any information that is automatically collected by us through the use of our programmed software when you access the website. This type of information is collected immediately a user accesses the website. This information may include the user's device IP Address, location, cookies and other tracking information. Non-personal information is majorly collected through your device with the help of our tracking tools such as cookies (see our Cookies Policy) and other third-party tracking technologies such as Google Analytics.`,
          },
          {
            key: "Aggregated Information:",
            text: `We collect aggregated information regarding the use of our website in order to determine the level of improvement. Aggregated information is numerical or non-numerical information that is compiled through technologies as reports or summaries for analytical purposes. This kind of information does not reveal the identity of any user. Aggregated information may include the (i) number of users that subscribed to our newsletter, (ii) number of visits to the website, (iii) most feature of the website accessed by users, and (iv) website/link a visitor navigates to our website from. We may share aggregated information with our affiliates and third parties to conduct general business analysis, which, in turn, helps us identify what area or feature to improve on the website.`,
          },
        ],
      },
    ],
  },
  {
    number: "3.",
    heading: "What Do We Use Information Collected For?",
    items: [
      {
        type: "para",
        text: `Any information collected by us is for a purpose in relation to the service we offer, including for providing the services.`,
      },
      {
        type: "bullet-bold",
        bullets: [
          { key: null, text: `To personalize user's experience and to allow us to deliver the type of service in which you are most interested.` },
          { key: "To Improve the website and our services:", text: `We collect aggregated information to troubleshoot errors, analyze performance, and improve the features and services generally provided by us;` },
          { key: "To comply with the law and protect our rights:", text: `In certain cases, we are required to collect personal information as required by law to protect our rights and other users in the event of claims or disputes;` },
          { key: "To communicate with you:", text: `We collect your email address and phone number in order to (i) contact you regarding our service to you, (ii) respond to your questions, complaints, and queries, and (iii) send you updates regarding our services or this Policy and other agreements;` },
          { key: "For other purposes through your consent:", text: `We may also process your information for some other reasons which your consent will be sought before we process such information. You may also withdraw your consent at any time.` },
        ],
      },
    ],
  },
  {
    number: "4.",
    heading: "Who Do We Share Information With?",
    items: [
      {
        type: "bold-para",
        key: "Disclosures under the Law and to Protect Rights:",
        text: `KYo may disclose personal information about you when such disclosures are believed to safeguard or protect our rights or other users; comply with the applicable law; to enforce our Terms and Conditions and the other agreements. These disclosures may include sharing information with law agencies, law enforcement and other organizations, responding to court orders and subpoenas for the protection of fraud and carrying out investigations against claims.`,
      },
      {
        type: "note",
        text: `Please note: The disclosure of your information under this paragraph will not occur unless it is backed in writing and from relevant agencies.`,
      },
      {
        type: "bold-para",
        key: "Disclosures under a Business Transfer:",
        text: `Business transfers mean any sale of KYo asset, a merger, consolidation, or acquisition. These are likely situations in the course of business. If any of these occurs, we will have no choice but to share information about you with the other company. For example, should KYo be acquired by another entity, your personal information will be accessed and controlled by the acquiring entity.`,
      },
      {
        type: "para",
        text: `If the transfer of information as described under this section is to countries outside the European Economic Area ("EEA") or the European Union ("EU"), we will ensure such transfer is done in accordance.`,
      },
    ],
  },
  {
    number: "5.",
    heading: "Cookies and Other Tracking Technologies",
    items: [
      {
        type: "para",
        text: `"Cookies" are small pieces of information that are stored by your browser on your computer's hard drive. We do not use cookies to collect or store any information about visitors or customers.`,
      },
    ],
  },
  {
    number: "6.",
    heading: "Built and Strong Legal Bases for Information Processing",
    items: [
      {
        type: "para",
        text: `When we process your personal information, we do so because of the following "Legal bases:"`,
      },
      {
        type: "labeled",
        items: [
          { label: "a) Legitimate Interest:", text: `We process personal information to help us conduct our business, but not when your rights or interest overrides our interest.` },
          { label: "b) Consent:", text: `You gave us your consent to process your personal information. You may withdraw your consent at any time.` },
          { label: "c) Contract Performance:", text: `We must process your information in order to honor the contract between us.` },
        ],
      },
    ],
  },
  {
    number: "7.",
    heading: "User Postings",
    items: [
      {
        type: "para",
        text: `KYo allows users to post reviews about their experience through our social media pages and email address. By posting reviews through our social media pages, you agree that your review will be public and any person who has access to our social media pages and groups will also see the content of your review. We require that you apply caution on the kind of personal information you attach to any public platform. We will not be responsible for privacy lost from the reviews you posted to our social media pages.`,
      },
    ],
  },
  {
    number: "8.",
    heading: "Marketing E-Mails (Newsletters)",
    items: [
      {
        type: "para",
        text: `Marketing Emails or Newsletters are the occasional messages sent to your email address for marketing purposes, including on offers, bonuses, promotions, and products which we think may interest you (collectively "Marketing Emails"). By submitting your email address during order checkout, you are by default added to our newsletter list. You may also subscribe to receive Marketing Emails by outrightly submitting your email address at the bottom of the website.`,
      },
    ],
  },
  {
    number: "9.",
    heading: "Your Rights Regarding The Information We Hold About You",
    items: [
      {
        type: "para",
        text: `According to the GDPR law of 2018, you have the following rights regarding the information we hold about you. These rights include:`,
      },
      {
        type: "labeled",
        items: [
          { label: "a. The right to refuse the submission of information:", text: `You may not provide any personal information required from you. However, you will not be able to use the services the information is meant for.` },
          { label: "b. The right to correct personal information at any time:", text: `Any information, including information related to personal and billing, can be corrected at any time.` },
          { label: "c. The right to refuse cookies from being set on your device browser:", text: `Most device browsers permit you to deactivate or reject cookies via their settings area. Also, there is a choice to directly reject cookies or allow from being set on the screen. This option pop-ups occasionally when you access the website. Please note that if you reject cookies, some of the website features/functions may not work properly for you.` },
        ],
      },
    ],
  },
  {
    number: "10.",
    heading: "How Long Do We Retain Your Information?",
    items: [
      {
        type: "para",
        text: `We only keep information about members on our database. We keep the information for as long as the website and services function. This information helps us to monitor the members we have and when such members subscribe to any plan. This allows us to monitor analytics, protect our rights in the event of claims, and help us to comply with applicable laws.`,
      },
    ],
  },
  {
    number: "11.",
    heading: "Your Rights to Your Personal Information",
    items: [
      {
        type: "para",
        text: `You have the right to:`,
      },
      {
        type: "bullet-bold",
        bullets: [
          { key: "Request access to your personal data:", text: `This enables you to receive a copy of the personal data we hold about you and to check that we are lawfully processing it. You may send an email requesting information as the personal data which we process.` },
          { key: "Right to information when collecting and processing personal data about you from publicly accessible or third-party sources:", text: `When this takes place, we will inform you, within a reasonable and practicable timeframe, about the third party or publicly accessible source from which we have collected your personal data.` },
          { key: "Request correction or rectification of the personal data that we hold about you:", text: `This enables you to have any incomplete or inaccurate data we hold about you corrected and/or updated, though we may need to verify the accuracy of the new data you provide to us. As mentioned, it is in your interest to keep us informed of any changes or updates to your personal data which occur during the course of your relationship with us.` },
          {
            key: "Request erasure of your personal data:",
            text: `This enables you to ask us to delete or remove personal data where:`,
            nested: [
              `there is no good reason for us continuing to process it;`,
              `you have successfully exercised your right to object to processing;`,
              `we may have processed your information unlawfully; or`,
              `we are required to erase your personal data to comply with local law`,
            ],
          },
        ],
      },
    ],
  },
  {
    number: "12.",
    heading: "Third-Party Content",
    items: [
      {
        type: "para",
        text: `Our websites may contain links, content, or adverts that are posted by third-party websites or companies not affiliated to KYo ("Third Party Content"). These Third-Party Content are not governed by this Policy; therefore, we do not guarantee the privacy practices of Third-Party Content neither do we guarantee that they are safe to access. Once you leave our websites or get redirected to a Third-Party Content, you bear the risk or become liable for any damage or loss you may suffer thereafter. We recommend that you read the privacy policies of Third-Party Content that you wish to access through our websites before submitting information to them.`,
      },
    ],
  },
  {
    number: "13.",
    heading: "Security",
    items: [
      {
        type: "para",
        text: `To reduce the risk of unauthorized access, maintain data accuracy, and secure the correct use of information, we use commercially reasonable physical, electronic, and managerial procedures to protect the information We collect. We also use Secure Sockets Layer (SSL) protocol on your account information and registration pages to protect personal information. We encrypt credit card numbers that you provide to us. No security system for data is completely secure.`,
      },
    ],
  },
  {
    number: "14.",
    heading: "Data Retention",
    items: [
      {
        type: "para",
        text: `When you purchase a membership plan through the Site, we will maintain your Information for our records unless and until you ask us to delete this information.`,
      },
    ],
  },
  {
    number: "15.",
    heading: "Changes to This Privacy Policy",
    items: [
      {
        type: "para",
        text: `This Privacy Policy is current as of the Effective Date. We may change this Privacy Policy from time to time, so please be sure to check back periodically. We will post any changes to this Privacy Policy on our Websites. If we make any material changes to this Privacy Policy, we will endeavor to provide you with notice in advance by sending notice to the email address of record.`,
      },
    ],
  },
  {
    number: "16.",
    heading: "Contact Us",
    items: [
      {
        type: "para",
        text: `If there are any questions regarding this privacy policy, you may contact us using the information:`,
        emailLink: { label: "privacy@kyoown.com", href: "mailto:privacy@kyoown.com" },
      },
    ],
  },
];

function renderItems(items) {
  return items.map((item, idx) => {
    if (item.type === "para") {
      return (
        <p key={idx} className="text-[14px] leading-relaxed mb-3 text-gray-600">
          {item.text}
          {item.emailLink && (
            <> <a href={item.emailLink.href} className="text-[#ED2E24] hover:underline">{item.emailLink.label}</a></>
          )}
        </p>
      );
    }

    if (item.type === "numbered") {
      return (
        <div key={idx} className="flex gap-3 mb-3">
          <span className="text-[14px] font-semibold text-gray-500 shrink-0 pt-[1px]">{item.label}</span>
          <p className="text-[14px] leading-relaxed text-gray-600">
            {item.text}
            {item.emailLink && (
              <> <a href={item.emailLink.href} className="text-[#ED2E24] hover:underline">{item.emailLink.label}</a></>
            )}
          </p>
        </div>
      );
    }

    if (item.type === "bullet-bold") {
      return (
        <ul key={idx} className="my-3 space-y-3 pl-1">
          {item.bullets.map((b, bi) => (
            <li key={bi} className="flex items-start gap-2.5 text-[14px] text-gray-600 leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#ED2E24] shrink-0" />
              <span>
                {b.key && <strong className="text-gray-800">{b.key} </strong>}
                {b.text}
                {b.nested && (
                  <ul className="mt-2 space-y-1.5 pl-1">
                    {b.nested.map((n, ni) => (
                      <li key={ni} className="flex items-start gap-2 text-[13px] text-gray-500">
                        <span className="mt-2 w-1 h-1 rounded-full bg-gray-400 shrink-0" />
                        {n}
                      </li>
                    ))}
                  </ul>
                )}
              </span>
            </li>
          ))}
        </ul>
      );
    }

    if (item.type === "bold-para") {
      return (
        <p key={idx} className="text-[14px] leading-relaxed mb-3 text-gray-600">
          <strong className="text-gray-800">{item.key} </strong>
          {item.text}
        </p>
      );
    }

    if (item.type === "note") {
      return (
        <p key={idx} className="text-[13px] leading-relaxed mb-3 text-gray-500 italic border-l-2 border-[#ED2E24] pl-3">
          {item.text}
        </p>
      );
    }

    if (item.type === "labeled") {
      return (
        <div key={idx} className="my-3 space-y-3">
          {item.items.map((li, li_idx) => (
            <div key={li_idx} className="flex gap-2.5 text-[14px] text-gray-600 leading-relaxed">
              <span className="font-semibold text-gray-700 shrink-0">{li.label}</span>
              <span>{li.text}</span>
            </div>
          ))}
        </div>
      );
    }

    return null;
  });
}

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="privacy-policy-page">
      <Navbar />

      <main className="flex-1">
        {/* Hero band */}
        <div className="bg-[#111111] pb-14 pt-[100px] px-5">
          <div className="max-w-3xl mx-auto">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-6 transition-colors"
              data-testid="privacy-back-btn"
            >
              <ArrowLeft size={15} />
              Back
            </button>
            <p className="text-[#ED2E24] text-xs font-semibold uppercase tracking-widest mb-3">Legal</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-3">
              Cyrus Field Technologies Inc<br className="hidden sm:block" /> And KYo Group Privacy Policy
            </h1>
            <p className="text-gray-400 text-sm">Last Updated on: Nov, 2026</p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-5 py-12 sm:py-16" data-testid="privacy-content">
          {SECTIONS.map((section) => (
            <section key={section.number} className="mb-10">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-100">
                {section.number} {section.heading}
              </h2>
              {renderItems(section.items)}
            </section>
          ))}

          {/* Contact strip */}
          <div className="mt-8 rounded-2xl bg-[#1a1a1a] px-8 py-8 text-center">
            <p className="text-white text-[15px] font-semibold mb-1">Privacy questions?</p>
            <p className="text-gray-400 text-[13px]">
              Contact us at{" "}
              <a href="mailto:privacy@kyoown.com" className="text-[#ED2E24] hover:underline">
                privacy@kyoown.com
              </a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
